<template>
    <div class="flex flex-col gap-4">
        <p class="body-small text-ink-muted">{{ t("journeys.discardGoalFeedback.prompt") }}</p>
        <BaseNotification v-if="feedbackError" theme="error" :text="t('journeys.discardGoalFeedback.saveError')" hide-dismiss />
        <div class="flex flex-col gap-2">
            <BaseCheckbox
                v-for="option in issueOptions"
                :key="option.value"
                :model-value="selectedIssues.includes(option.value)"
                shape="square"
                :disabled="isBusy"
                @update:model-value="setIssue(option.value, $event === true)">
                <span class="body-small text-ink">{{ option.label }}</span>
            </BaseCheckbox>
        </div>
        <BaseTextarea
            v-model:input="comment"
            :aria-label="t('journeys.discardGoalFeedback.additionalCommentsAria')"
            rows="3"
            :placeholder="t('journeys.discardGoalFeedback.commentsPlaceholder')"
            disable-autosize
            :disabled="isBusy"
            class="resize-none rounded-lg border border-control-border bg-control-bg p-2.5 text-control-text placeholder:text-control-placeholder"
            :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'" />
        <div class="flex items-center justify-end gap-3 pt-2">
            <BaseButton theme="neutral" size="sm" :disabled="isBusy" @click="emit('cancel')">{{ t("common.cancel") }}</BaseButton>
            <BaseButton theme="danger" size="sm" :disabled="isBusy" :aria-busy="isBusy" @click="handleSubmit">
                {{ isBusy ? t("journeys.discardGoalFeedback.discarding") : t("journeys.discardGoalFeedback.discardGoal") }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import type { GoalFeedbackRequest } from "~vue/types/goals";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ goalId: string; isDeleting?: boolean }>();
const emit = defineEmits<{ discarded: []; cancel: []; "update:processing": [value: boolean] }>();

const { t } = useI18n();

const isProcessing = ref(false);
const selectedIssues = ref<string[]>([]);
const comment = ref("");
const isBusy = computed(() => isProcessing.value || props.isDeleting);
const lastSubmittedFeedback = ref<string | null>(null);

// Locale catalogue can finish loading after mount, so option labels must stay reactive to the active locale.
const issueOptions = computed(() => [
    { value: "not_relevant", label: t("journeys.discardGoalFeedback.issues.notRelevant") },
    { value: "deprioritized", label: t("journeys.discardGoalFeedback.issues.deprioritized") },
    { value: "not_a_goal", label: t("journeys.discardGoalFeedback.issues.notAGoal") },
]);

const hasFeedback = computed(() => selectedIssues.value.length > 0 || comment.value.trim() !== "");

const feedbackBody = computed<GoalFeedbackRequest>(() => ({
    value: -1,
    issues: selectedIssues.value,
    comment: comment.value.trim() || null,
}));

const { execute: submitFeedback, error: feedbackError } = useFetch(
    computed(() => `/v1/goals/${props.goalId}/feedback`),
    { immediate: false },
)
    .post(feedbackBody)
    .json();

function setIssue(issue: string, checked: boolean) {
    if (!isBusy.value) {
        selectedIssues.value = checked ? [...selectedIssues.value, issue] : selectedIssues.value.filter((value) => value !== issue);
    }
}

async function handleSubmit() {
    if (isBusy.value) {
        return;
    }
    isProcessing.value = true;
    emit("update:processing", true);
    try {
        const feedback = JSON.stringify(feedbackBody.value);
        if (hasFeedback.value && lastSubmittedFeedback.value !== feedback) {
            await submitFeedback();
            if (feedbackError.value) {
                return;
            }
            lastSubmittedFeedback.value = feedback;
        }
        emit("discarded");
    } finally {
        isProcessing.value = false;
        emit("update:processing", false);
    }
}
</script>
